import React, { useState, useEffect } from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { UserList } from './UserList';
import { UserForm } from './UserForm';
import { supabaseAdmin } from '../../config/supabase';
import { User } from '../../types';
import { useStore } from '../../store/useStore';
import toast from 'react-hot-toast';
import { generateAvatarUrl } from '../../utils/avatar';
import { UserPlus } from 'lucide-react';
import { logger } from '../../utils/logger';

export const UserManagement: React.FC = () => {
  const [isAddingUser, setIsAddingUser] = useState(false);
  const [editingUser, setEditingUser] = useState<User | null>(null);
  const { availableUsers, addEmployee, removeEmployee, updateEmployee } = useStore();
  const [isLoading, setIsLoading] = useState(true);

  React.useEffect(() => {
    const loadUsers = async () => {
      try {
        setIsLoading(true);
        logger.info('🔥 Loading users...');

        const { data: profiles, error } = await supabaseAdmin
          .from('profiles')
          .select('*')
          .order('name');

        if (error) {
          logger.error('❌ Failed to fetch profiles:', error);
          throw error;
        }

        logger.info('✅ Profiles fetched:', profiles?.length || 0);

        // Map profiles to User type
        const users = profiles?.map(profile => ({
          id: profile.id,
          email: profile.email,
          name: profile.name,
          role: profile.role,
          department: profile.department,
          avatar: profile.avatar || generateAvatarUrl(profile.email),
          phone: profile.phone
        })) || [];

        // Update store with fetched users
        users.forEach(user => addEmployee(user));
      } catch (error) {
        console.error('Failed to load users:', error);
        toast.error('Failed to load users');
      } finally {
        setIsLoading(false);
      }
    };

    loadUsers();

    // Set up real-time subscription
    const subscription = supabaseAdmin
      .channel('profiles_changes')
      .on('postgres_changes', { 
        event: '*', 
        schema: 'public', 
        table: 'profiles' 
      }, async (payload) => {
        // Handle profile changes
        if (payload.eventType === 'INSERT' || payload.eventType === 'UPDATE') {
          const profile = payload.new;
          const user: User = {
            id: profile.id,
            email: profile.email,
            name: profile.name,
            role: profile.role,
            department: profile.department,
            avatar: profile.avatar || generateAvatarUrl(profile.email),
            phone: profile.phone
          };
          
          if (payload.eventType === 'INSERT') {
            addEmployee(user);
          } else {
            updateEmployee(user);
          }
        } else if (payload.eventType === 'DELETE') {
          removeEmployee(payload.old.id);
        }
      })
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [addEmployee, removeEmployee, updateEmployee]);

  const handleAddUser = async (userData: Omit<User, 'id' | 'avatar'> & { password: string }) => {
    try {
      setIsLoading(true);
      logger.info('Creating new user:', { email: userData.email });

      // Step 1: Create auth user with admin privileges
      const { data: { user }, error: authError } = await supabaseAdmin.auth.admin.createUser({
        email: userData.email,
        password: userData.password,
        email_confirm: true,
        user_metadata: {
          name: userData.name,
          role: userData.role,
          department: userData.department
        }
      });

      if (authError || !user) {
        throw authError || new Error('Failed to create auth user');
      }

      logger.info('Auth user created:', { userId: user.id });

      // Step 2: Create profile
      const avatar = generateAvatarUrl(userData.email);
      
      // Optimistically update local state
      const newUser: User = {
        id: user.id,
        email: userData.email,
        name: userData.name,
        role: userData.role,
        department: userData.department,
        phone: userData.phone,
        avatar
      };
      
      addEmployee(newUser);

      const { error: profileError } = await supabaseAdmin
        .from('profiles')
        .insert({
          id: user.id,
          email: userData.email,
          name: userData.name,
          role: userData.role,
          department: userData.department,
          phone: userData.phone,
          avatar,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        });

      if (profileError) {
        // Rollback optimistic update
        removeEmployee(user.id);
        // Cleanup: Delete auth user if profile creation fails
        await supabaseAdmin.auth.admin.deleteUser(user.id);
        throw profileError;
      }

      logger.info('Profile created successfully');
      setIsAddingUser(false);
      toast.success('User added successfully');
      toast.success('Welcome email sent to user');

    } catch (error) {
      logger.error('Failed to add user:', error);
      const message = error instanceof Error ? error.message : 'Failed to add user';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateUser = async (userData: Partial<User>) => {
    if (!editingUser) return;
    
    try {
      setIsLoading(true);
      
      // Optimistically update local state
      const updatedUser: User = {
        ...editingUser,
        ...userData,
      };
      updateEmployee(updatedUser);
      
      // Update profile in Supabase
      const { error } = await supabaseAdmin
        .from('profiles')
        .update({
          name: userData.name,
          role: userData.role,
          department: userData.department,
          phone: userData.phone,
          updated_at: new Date().toISOString()
        })
        .eq('id', editingUser.id);

      if (error) {
        // Rollback optimistic update
        updateEmployee(editingUser);
        throw error;
      }

      setEditingUser(null);
      toast.success('User updated successfully');
      
    } catch (error) {
      console.error('Failed to update user:', error);
      const message = error instanceof Error ? error.message : 'Failed to update user';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    if (!confirm('Are you sure you want to delete this user?')) return;
    
    try {
      setIsLoading(true);
      
      // Optimistically remove from local state
      const userToDelete = availableUsers.find(u => u.id === userId);
      if (!userToDelete) throw new Error('User not found');
      
      removeEmployee(userId);
      
      // Delete user from auth
      const { error: authError } = await supabaseAdmin.auth.admin.deleteUser(userId);
      if (authError) {
        // Rollback optimistic delete
        if (userToDelete) {
          addEmployee(userToDelete);
        }
        throw authError;
      }

      toast.success('User deleted successfully');
      
    } catch (error) {
      console.error('Failed to delete user:', error);
      const message = error instanceof Error ? error.message : 'Failed to delete user';
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <Card className="p-6">
        <div className="text-center py-8">Loading users...</div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold">User Management</h2>
        <Button
          variant="primary"
          size="sm"
          onClick={() => setIsAddingUser(true)}
          icon={<UserPlus className="w-4 h-4" />}
          className="shadow-sm hover:shadow-md transition-shadow"
        >
          Add User
        </Button>
      </div>

      {isAddingUser && (
        <div className="mb-6">
          <UserForm
            onSubmit={handleAddUser}
            onCancel={() => setIsAddingUser(false)}
          />
        </div>
      )}

      {editingUser && (
        <div className="mb-6">
          <UserForm
            user={editingUser}
            onSubmit={handleUpdateUser}
            onCancel={() => setEditingUser(null)}
          />
        </div>
      )}

      <UserList
        users={availableUsers}
        onEdit={setEditingUser}
        onDelete={handleDeleteUser}
      />
    </Card>
  );
};